import axios from 'axios';
import config from './config';
import { getToken } from './auth';
import { downloadFile } from './utils/excelExport';

// Axios instance with base config
const api = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 10000,
});

// Attach Bearer token to every request if available
api.interceptors.request.use(
  (req) => {
    const token = getToken();
    if (token) {
      req.headers['Authorization'] = `Bearer ${token}`;
    }
    return req;
  },
  (error) => Promise.reject(error)
);

// Offline support: fallback to localStorage cache for GET requests
api.interceptors.response.use(
  (response) => {
    // Cache GET responses
    if (response.config.method === 'get') {
      localStorage.setItem(
        `cache:${response.config.url}`,
        JSON.stringify({
          data: response.data,
          timestamp: Date.now(),
        })
      );
    }
    return response;
  },
  async (error) => {
    // If offline and GET, try to serve from cache
    if (
      error.config &&
      error.config.method === 'get' &&
      (!window.navigator.onLine || error.message === 'Network Error')
    ) {
      const cached = localStorage.getItem(`cache:${error.config.url}`);
      if (cached) {
        return { ...error.response, data: JSON.parse(cached).data };
      }
    }

    // Global handler: logout and redirect on 401 Unauthorized
    if (error.response && error.response.status === 401) {
      // Remove token and user info
      localStorage.removeItem('token');
      localStorage.removeItem('token_id');
      sessionStorage.removeItem('userInfo');
      // Redirect to login
      window.location.href = '/login';
      return; // Prevent further error propagation
    }
    return Promise.reject(error);
  }
);

// Diary API functions
export const getDiaryAttachments = async (diaryId) => {
  try {
    const response = await api.post(`/diary/${diaryId}/attachments`);
    return response.data;
  } catch (error) {
    console.error('Error fetching diary attachments:', error);
    throw error;
  }
};

// DEPRECATED: Prefer getTrialDiaryEntry(trialId, diaryId). This remains only as a fallback
export const getDiaryEntry = async (diaryId) => {
  console.warn('[api] getDiaryEntry(diaryId) is deprecated. Use getTrialDiaryEntry(trialId, diaryId) instead. Called with id:', diaryId);
  try {
    const response = await api.post(`/diary/${diaryId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching diary entry (legacy /diary/:id):', error);
    throw error;
  }
};

// Preferred endpoint (scoped by trial) for fetching a single diary entry
export const getTrialDiaryEntry = async (trialId, diaryId) => {
  try {
    const response = await api.post(`/trials/${trialId}/diaries/${diaryId}`);
    return response.data;
  } catch (error) {
    // Fallback to legacy endpoint if the new scoped route fails (e.g., backend not updated yet)
    console.warn('Scoped trial diary endpoint failed, falling back to legacy /diary/:id', error?.response?.status);
    return getDiaryEntry(diaryId);
  }
};

// Generic attachments API - can be used as fallback
export const getAttachments = async (modelType, modelId) => {
  try {
    const response = await api.post(`/attachments`, {
      model_type: modelType,
      model_id: modelId
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching ${modelType} attachments:`, error);
    throw error;
  }
};

// Downloads a task's or trial's file as stored (full quality) under its original filename. The token
// goes in the header, so there is no public link. Longer timeout: a full-size photo on a field connection.
export const downloadAttachment = async (attachment) => {
  const response = await api.get(`/attachments/${attachment.id}/download`, { responseType: 'blob', timeout: 120000 });
  if (!response) return; // 401: the interceptor is already logging out
  // As the API names it: trial nos like "37 15 01/ESG15-001" put a slash in the name
  downloadFile(response.data, (attachment.original_filename || attachment.filename).replace(/[/\\]/g, '_'));
};

export default api;
